import { Body, breakpoints, Button, colors, Spacer } from '@spritz-finance/ui'
import styled from 'styled-components'
import { FadePresence } from '../../../../components/FadePresence'
import { Column } from '../../../../components/Layout/Column'
import { Row } from '../../../../components/Layout/Row'
import { SimpleLoader } from '../../../../components/SimpleLoader/SimpleLoader'
import { FeedbackForm, useFeedback } from '../../../../context/useFeedback'
import {
    BillType,
    MerchantSummaryFragment,
    useMerchantsQuery,
    usePopularMerchantsQuery,
} from '../../../../graphql/generated/graphql'
import { useLinkBillContext } from './LinkBill'

const CircleImage = styled.img`
    background: #d6d6d6;
    border-radius: 50%;
    width: 32px;
    height: 32px;
    object-fit: contain;
`

const Circle = styled.div`
    background: #d6d6d6;
    border-radius: 50%;
    width: 32px;
    height: 32px;
`

const MerchantContainer = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding: 23px 0px;
    border: 1px solid ${colors.charcoal};
    border-radius: 10px;
    padding: 8px;
    min-height: 114px;
    transition: all 0.3s ease-in-out;

    &:hover {
        cursor: pointer;
        border-color: var(--text-primary, transparent);
        box-shadow: 10px 10px 40px 0px var(--card-hover-shadow, rgba(0, 0, 0, 0.15));
    }

    ${breakpoints.small} {
        width: 113px;
        min-height: 105px;
    }
`

export const MerchantLogo = ({ merchant }: { merchant: MerchantSummaryFragment }) => {
    return merchant.logo ? <CircleImage src={merchant.logo} /> : <Circle />
}

const MerchantCard = ({ merchant }: { merchant: MerchantSummaryFragment }) => {
    const { addAccountForMerchant } = useLinkBillContext()
    return (
        <MerchantContainer onClick={() => addAccountForMerchant(merchant)}>
            <MerchantLogo merchant={merchant} />
            <Spacer height={8} />
            <Body.Bold size={14} style={{ textAlign: 'center', wordBreak: 'break-word' }}>
                {merchant.name}
            </Body.Bold>
        </MerchantContainer>
    )
}

const MerchantsGrid = styled.div`
    width: 100%;
    display: grid;
    column-gap: 20px;
    row-gap: 20px;
    grid-template-columns: 1fr 1fr 1fr 1fr;

    ${breakpoints.small} {
        justify-items: center;
        grid-template-columns: 1fr 1fr 1fr 1fr;
    }
    ${breakpoints.xsmall} {
        justify-items: center;
        grid-template-columns: 1fr 1fr 1fr;
    }
    @media (max-width: 480px) {
        grid-template-columns: 1fr 1fr;
    }
`

const PopularMerchants = ({ type }: { type?: BillType }) => {
    const { data, loading } = usePopularMerchantsQuery({ variables: { type } })

    const merchants = data?.popularMerchants ?? []
    const foundMerchants = merchants.length > 0

    return (
        <>
            <Row align="center">{loading && <SimpleLoader visible={loading} />}</Row>
            {foundMerchants && (
                <>
                    <Spacer height={24} />
                    <Row>
                        <Body.SmBold size={14}>
                            Choose from one of our most popular bills below, or use the search box
                            above
                        </Body.SmBold>
                    </Row>
                </>
            )}
            <Spacer height={16} />
            <MerchantsGrid>
                {merchants.slice(0, 8).map((merchant) => (
                    <MerchantCard key={merchant.id} merchant={merchant} />
                ))}
            </MerchantsGrid>
        </>
    )
}

const MerchantSearch = ({
    searchTerm,
    onBack,
    type,
}: {
    searchTerm: string
    onBack: () => void
    type?: BillType
}) => {
    const { openFeedbackForm } = useFeedback()
    const { data, loading: merchantsLoading } = useMerchantsQuery({
        variables: {
            name: searchTerm,
            type,
        },
        fetchPolicy: 'cache-first',
    })
    const merchants = data?.merchants ?? []

    const noMerchants = !merchantsLoading && !merchants.length
    const filteredMerchants = merchants
    const renderKey = filteredMerchants.map((merchant) => merchant.id).join('')

    return (
        <>
            <Spacer height={4} />
            <Row align="center">
                <SimpleLoader visible={merchantsLoading} />
            </Row>
            <FadePresence renderKey={renderKey}>
                {noMerchants ? (
                    <>
                        <Column align="center">
                            <Body.SmBold>Uh-oh!</Body.SmBold>
                            <Spacer height={16} />
                            <Body.SmBold>
                                We don’t seem to have any results for “{searchTerm}”. Would you like
                                to request this specific bill?
                            </Body.SmBold>
                            <Spacer height={56} />
                            <Button.CTA
                                color="#FF832C"
                                onClick={() =>
                                    openFeedbackForm(FeedbackForm.RequestMerchant, searchTerm)
                                }
                            >
                                Request "{searchTerm}"
                            </Button.CTA>
                            <Spacer height={32} />
                            <Button.Text onClick={onBack}>Search for another bill</Button.Text>
                        </Column>
                    </>
                ) : (
                    <>
                        <MerchantsGrid>
                            {filteredMerchants.map((merchant) => (
                                <MerchantCard key={merchant.id} merchant={merchant} />
                            ))}
                        </MerchantsGrid>
                        {!merchantsLoading && (
                            <>
                                <Spacer height={16} />
                                <Row align="center">
                                    <Body.Med size={14}>Don't see your bill?</Body.Med>
                                    <Button.Text
                                        onClick={() => {
                                            window.open('https://spritz.canny.io/bills')
                                        }}
                                    >
                                        Request your bill provider here.
                                    </Button.Text>
                                </Row>
                            </>
                        )}
                    </>
                )}
            </FadePresence>
        </>
    )
}

export const MerchantSelect = ({
    searchTerm,
    onBack,
}: {
    searchTerm: string
    onBack: () => void
}) => {
    const { selectBillType } = useLinkBillContext()
    const type = selectBillType?.type
    const searching = searchTerm.length > 0

    return (
        <FadePresence renderKey={searching} style={{ width: '100%' }}>
            {searching ? (
                <MerchantSearch searchTerm={searchTerm} onBack={onBack} type={type} />
            ) : (
                <PopularMerchants type={type} />
            )}
        </FadePresence>
    )
}
